import axios from "axios";
import { legacyParamsSerializer } from "./legacyParamsSerializer";
import { invalidatePendingRefresh, markLoginAttempt } from "./authSession";

const TOKEN_KEY = "naezip_to_ac_se";
const SAME_ORIGIN_AUTH_HOST = "www.naezipscan.com";

const AUTH_ROUTES = {
  USER_KAKAO_LOGIN: {
    sameOrigin: "/api/auth/login/kakao",
    direct: "/api/v1/user/login/kakao",
  },
  REFRESH: {
    sameOrigin: "/api/auth/refresh",
    direct: "/api/v1/user/auth/refresh",
  },
  EXCHANGE: {
    sameOrigin: "/api/auth/exchange",
    direct: "/api/v1/user/auth/exchange",
  },
  LOGOUT: {
    sameOrigin: "/api/auth/logout",
    direct: "/api/v1/user/logout",
  },
  REALTOR_REGISTER: {
    sameOrigin: "/api/auth/realtor/register",
    direct: "/api/v1/realtor/auth/register",
  },
  REALTOR_LOGIN: {
    sameOrigin: "/api/auth/realtor/login",
    direct: "/api/v1/realtor/auth/login",
  },
  SHERLOCK_REGISTER: {
    sameOrigin: "/api/auth/sherlock/register",
    direct: "/api/v1/sherlock/auth/register",
  },
  SHERLOCK_LOGIN: {
    sameOrigin: "/api/auth/sherlock/login",
    direct: "/api/v1/sherlock/auth/login",
  },
  DABANG_EXCHANGE: {
    sameOrigin: "/api/auth/dabang/exchange",
    direct: "/api/v1/partner/dabang/exchange",
  },
};

const authInstance = axios.create({
  withCredentials: true,
  // refresh/login 요청이 네트워크 단절에서 영구 대기해 인증 큐를 붙잡지 않게 한다.
  timeout: 12000,
  paramsSerializer: { serialize: legacyParamsSerializer },
});

authInstance.interceptors.request.use(config => {
  try {
    const accessToken = window.localStorage.getItem(TOKEN_KEY);
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  } catch (error) {
    // 저장소 접근 불가 환경에서는 쿠키만으로 요청한다.
  }
  return config;
});

const isSameOriginAuthHost = () => {
  return typeof window !== "undefined" && window.location?.hostname === SAME_ORIGIN_AUTH_HOST;
};

// www 이외 환경은 기존 API 호스트를 유지한다. jasanoneclick, 로컬, Vercel 프리뷰가
// 운영 API로 고정되거나 존재하지 않는 /api/auth rewrite를 타는 회귀를 막는다.
const resolveDirectApiHost = () => {
  if (process.env.VUE_APP_MRI_BRIDGE === "1") {
    try {
      const override = window.localStorage.getItem("mri_api_host");
      if (override && /^https:\/\//.test(override)) return override.replace(/\/$/, "");
    } catch (error) {
      // 저장소 접근 불가 — 빌드 환경의 API 호스트를 사용한다.
    }
  }
  return process.env.VUE_APP_HOST;
};

const requestAuthWithMode = (route, config, sameOrigin) => {
  return authInstance.request({
    ...config,
    url: sameOrigin ? route.sameOrigin : route.direct,
    baseURL: sameOrigin ? "" : resolveDirectApiHost(),
  });
};

const requestAuth = (route, config) => {
  return requestAuthWithMode(route, config, isSameOriginAuthHost());
};

const settleAuthRequest = request => {
  return Promise.resolve()
    .then(request)
    .then(
      response => ({ response }),
      error => ({ error }),
    );
};

export const requestUserKakaoLogin = (config = {}) => {
  // App.vue의 초기 API가 이전 세션으로 refresh 중이어도 새 OAuth 세션을 지우지 못하게 한다.
  markLoginAttempt();
  return requestAuth(AUTH_ROUTES.USER_KAKAO_LOGIN, { method: "get", ...config });
};

export const requestRefreshToken = (config = {}) =>
  requestAuth(AUTH_ROUTES.REFRESH, { method: "get", ...config });

export const requestExchangeToken = () => requestAuth(AUTH_ROUTES.EXCHANGE, { method: "get" });

export const requestRealtorAuthRegister = data =>
  requestAuth(AUTH_ROUTES.REALTOR_REGISTER, { method: "post", data });

export const requestRealtorAuthLogin = data =>
  requestAuth(AUTH_ROUTES.REALTOR_LOGIN, { method: "post", data });

export const requestSherlockAuthRegister = data =>
  requestAuth(AUTH_ROUTES.SHERLOCK_REGISTER, { method: "post", data });

export const requestSherlockAuthLogin = data =>
  requestAuth(AUTH_ROUTES.SHERLOCK_LOGIN, { method: "post", data });

export const requestDabangExchange = data =>
  requestAuth(AUTH_ROUTES.DABANG_EXCHANGE, { method: "post", data });

export const requestLogout = async () => {
  invalidatePendingRefresh();
  const config = { method: "post", data: {} };
  if (!isSameOriginAuthHost()) {
    return requestAuthWithMode(AUTH_ROUTES.LOGOUT, config, false);
  }

  // 마이그레이션 전에 로그인한 브라우저에는 API 도메인의 구형 쿠키가 남아 있다.
  // www 쿠키와 구형 쿠키를 각각 지우되, 한쪽 실패가 다른 쪽 로그아웃 성공을 덮지 않게 한다.
  const [sameOriginResult, legacyResult] = await Promise.all([
    settleAuthRequest(() => requestAuthWithMode(AUTH_ROUTES.LOGOUT, config, true)),
    settleAuthRequest(() => requestAuthWithMode(AUTH_ROUTES.LOGOUT, config, false)),
  ]);

  if (sameOriginResult.response) return sameOriginResult.response;
  if (legacyResult.response) return legacyResult.response;
  throw sameOriginResult.error || legacyResult.error;
};
